import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetSmtpSettings, useUpdateSmtpSettings, useListCardholders, useUpdateCardholder } from "@workspace/api-client-react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Mail, Server, Users, User, Save, Trash2, Route } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

async function settingsRequest(path: string, init?: RequestInit) {
  const res = await fetch(`${BASE}/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", ...init?.headers },
    ...init,
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "Request failed");
  return res.json();
}

export default function Settings() {
  const { data: smtpData, isLoading: smtpLoading } = useGetSmtpSettings();
  const updateSmtpMutation = useUpdateSmtpSettings();
  const { toast } = useToast();

  const [smtpForm, setSmtpForm] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    fromEmail: ""
  });

  useEffect(() => {
    if (smtpData) {
      setSmtpForm({
        host: smtpData.host || "",
        port: smtpData.port || 587,
        username: smtpData.username || "",
        password: "", // Keep empty for security
        fromEmail: smtpData.fromEmail || ""
      });
    }
  }, [smtpData]);

  const handleSmtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateSmtpMutation.mutate({ data: smtpForm }, {
      onSuccess: () => toast({ title: "SMTP settings saved" }),
      onError: (err) => toast({ title: "Error saving settings", variant: "destructive" })
    });
  };

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Configure email reminders and map cardholders.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* SMTP Configuration */}
        <Card className="h-fit">
          <CardHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Server className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle>SMTP Configuration</CardTitle>
                <CardDescription>Setup email server for sending reminders.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {smtpLoading ? (
              <div className="py-8 text-center text-slate-500">Loading...</div>
            ) : (
              <form onSubmit={handleSmtpSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">SMTP Host</label>
                  <input
                    type="text"
                    required
                    value={smtpForm.host}
                    onChange={e => setSmtpForm({...smtpForm, host: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                    placeholder="smtp.example.com"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-700">Port</label>
                    <input
                      type="number"
                      required
                      value={smtpForm.port}
                      onChange={e => setSmtpForm({...smtpForm, port: parseInt(e.target.value) || 587})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-700">From Email</label>
                    <input
                      type="email"
                      required
                      value={smtpForm.fromEmail}
                      onChange={e => setSmtpForm({...smtpForm, fromEmail: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                      placeholder="finance@company.com"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">Username</label>
                  <input
                    type="text"
                    required
                    value={smtpForm.username}
                    onChange={e => setSmtpForm({...smtpForm, username: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">Password</label>
                  <input
                    type="password"
                    placeholder={smtpData?.configured ? "•••••••• (leave blank to keep current)" : "Enter SMTP password"}
                    value={smtpForm.password}
                    onChange={e => setSmtpForm({...smtpForm, password: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                  />
                </div>
                <Button type="submit" className="w-full mt-4" disabled={updateSmtpMutation.isPending}>
                  {updateSmtpMutation.isPending ? "Saving..." : "Save SMTP Settings"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Cardholders mapping */}
        <CardholderSettings />
        <ReminderRoutingRules />
      </div>
    </Layout>
  );
}

type ReminderRule = {
  id: number;
  descriptionSubstring: string;
  targetUserId: number;
  targetUserName: string;
  targetUserEmail: string;
};

function ReminderRoutingRules() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [descriptionSubstring, setDescriptionSubstring] = useState("");
  const [targetUserId, setTargetUserId] = useState("");
  const rules = useQuery<ReminderRule[]>({
    queryKey: ["expense-reminder-routing-rules"],
    queryFn: () => settingsRequest("/settings/reminder-routing-rules"),
  });
  const users = useQuery<Array<{ id: number; name: string; email: string }>>({
    queryKey: ["expense-reminder-routing-users"],
    queryFn: () => settingsRequest("/settings/reminder-routing-users"),
  });
  const addRule = useMutation({
    mutationFn: () => settingsRequest("/settings/reminder-routing-rules", {
      method: "POST",
      body: JSON.stringify({ descriptionSubstring, targetUserId: Number(targetUserId) }),
    }),
    onSuccess: () => {
      setDescriptionSubstring("");
      setTargetUserId("");
      queryClient.invalidateQueries({ queryKey: ["expense-reminder-routing-rules"] });
      toast({ title: "Reminder routing rule added" });
    },
    onError: (error: Error) => toast({ title: error.message, variant: "destructive" }),
  });
  const deleteRule = useMutation({
    mutationFn: (id: number) => settingsRequest(`/settings/reminder-routing-rules/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["expense-reminder-routing-rules"] }),
  });

  return (
    <Card className="h-fit lg:col-span-2">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
            <Route className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <CardTitle>Reminder Routing Rules</CardTitle>
            <CardDescription>Send reminders to another user when payment descriptions contain specified text.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
          <input value={descriptionSubstring} onChange={(e) => setDescriptionSubstring(e.target.value)}
            placeholder="Description contains, e.g. REPLIT.COM"
            className="px-4 py-2 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-primary/50" />
          <select value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary/50">
            <option value="">Send reminder to…</option>
            {users.data?.map((user) => <option key={user.id} value={user.id}>{user.name} ({user.email})</option>)}
          </select>
          <Button onClick={() => addRule.mutate()} disabled={!descriptionSubstring.trim() || !targetUserId || addRule.isPending}>
            Add rule
          </Button>
        </div>
        <p className="text-xs text-slate-500">Matching is not case-sensitive. The first matching rule is used; unmatched payments still go to the cardholder.</p>
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl">
          {rules.isLoading ? <div className="p-4 text-sm text-slate-500">Loading rules…</div>
            : rules.data?.length ? rules.data.map((rule) => (
              <div key={rule.id} className="flex items-center justify-between gap-4 p-4">
                <div>
                  <div className="font-medium text-slate-900">Description contains “{rule.descriptionSubstring}”</div>
                  <div className="text-sm text-slate-500">Send to {rule.targetUserName} ({rule.targetUserEmail})</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteRule.mutate(rule.id)} aria-label={`Delete ${rule.descriptionSubstring} rule`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            )) : <div className="p-4 text-sm text-slate-500">No routing rules yet.</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function CardholderSettings() {
  const { data: cardholders, isLoading } = useListCardholders();
  const updateMutation = useUpdateCardholder();
  const { toast } = useToast();
  
  // Local state for editing emails
  const [emails, setEmails] = useState<Record<number, string>>({});

  useEffect(() => {
    if (cardholders) {
      const initialEmails: Record<number, string> = {};
      cardholders.forEach(c => {
        initialEmails[c.id] = c.email || "";
      });
      setEmails(initialEmails);
    }
  }, [cardholders]);

  const handleSave = (id: number) => {
    updateMutation.mutate({ id, data: { email: emails[id] } }, {
      onSuccess: () => toast({ title: "Email saved" })
    });
  };

  return (
    <Card className="h-fit">
      <CardHeader>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
            <Users className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <CardTitle>Cardholder Emails</CardTitle>
            <CardDescription>Map extracted names to email addresses for reminders.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-8 text-center text-slate-500">Loading cardholders...</div>
        ) : cardholders?.length === 0 ? (
          <div className="py-8 text-center text-slate-500 bg-slate-50 rounded-xl border border-slate-100">
            No cardholders found. Upload statements first.
          </div>
        ) : (
          <div className="space-y-4 divide-y divide-slate-100">
            {cardholders?.map(cardholder => (
              <div key={cardholder.id} className="pt-4 first:pt-0">
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="font-semibold text-slate-900">{cardholder.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      value={emails[cardholder.id] || ""}
                      onChange={(e) => setEmails({...emails, [cardholder.id]: e.target.value})}
                      placeholder="employee@company.com"
                      className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                    />
                  </div>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={() => handleSave(cardholder.id)}
                    disabled={updateMutation.isPending && updateMutation.variables?.id === cardholder.id}
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
